import { useEffect, useState, useCallback } from 'react';
import {
    Grid, Box, Typography, Button, Alert, Skeleton,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAddAlt1';
import LoginIcon from '@mui/icons-material/Login';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { Surface, SectionHeader, StatCard } from '../../components/primitives';
import { adminFetch } from '../../utils/authUtils';

// ── formatting helpers ───────────────────────────────────────────────────

const fmtUsd = (cents) => {
    if (cents == null) return '—';
    return new Intl.NumberFormat('en-US', {
        style: 'currency', currency: 'USD', maximumFractionDigits: 0,
    }).format(cents / 100);
};

const fmtNum = (n) => {
    if (n == null) return '—';
    return Number(n).toLocaleString();
};

const fmtRelative = (unixSec) => {
    if (!unixSec) return 'never';
    const ageSec = Math.floor(Date.now() / 1000) - unixSec;
    if (ageSec < 60) return `${ageSec}s ago`;
    if (ageSec < 3600) return `${Math.floor(ageSec / 60)}m ago`;
    return `${Math.floor(ageSec / 3600)}h ago`;
};

// ── component ────────────────────────────────────────────────────────────

export default function AnalyticsPanel() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async (refresh = false) => {
        if (refresh) setRefreshing(true);
        else setLoading(true);
        setError(null);
        try {
            const resp = await adminFetch('/admin/analytics', refresh ? { refresh: true } : {});
            setData(resp);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => { load(false); }, [load]);

    if (loading) {
        return (
            <Grid container spacing={2}>
                {[...Array(8)].map((_, i) => (
                    <Grid item xs={6} md={3} key={i}>
                        <Skeleton variant="rectangular" height={92} sx={{ borderRadius: 1.5 }} />
                    </Grid>
                ))}
            </Grid>
        );
    }

    if (error) {
        return (
            <>
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                <Button onClick={() => load(true)} variant="outlined">Retry</Button>
            </>
        );
    }

    const users = data?.users || {};
    const groups = users.by_group || {};
    const signups = data?.signups || {};
    const logins = data?.logins || {};
    const revenue = data?.revenue || {};

    return (
        <>
            {/* Header: cache freshness + refresh */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
            }}>
                <Typography variant="caption" sx={{ color: 'text.muted' }}>
                    Updated {fmtRelative(data?._computed_at)} · 5-minute cache
                </Typography>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
                    onClick={() => load(true)}
                    disabled={refreshing}
                >
                    {refreshing ? 'Refreshing…' : 'Refresh'}
                </Button>
            </Box>

            {/* Users */}
            <SectionHeader icon={PeopleIcon}>Users</SectionHeader>
            <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={6} md={3}>
                    <StatCard label="Total" value={fmtNum(users.total)} mono />
                </Grid>
                <Grid item xs={6} md={3}>
                    <StatCard label="Free" value={fmtNum(groups.Free)} mono />
                </Grid>
                <Grid item xs={6} md={3}>
                    <StatCard label="Pro" value={fmtNum(groups.Premium)} mono />
                </Grid>
                <Grid item xs={6} md={3}>
                    <StatCard label="Comp" value={fmtNum(groups.Comp)} mono />
                </Grid>
            </Grid>

            {/* Signups */}
            <SectionHeader icon={PersonAddIcon}>Signups</SectionHeader>
            <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={4}>
                    <StatCard label="Last 24h" value={fmtNum(signups.last_24h)} mono />
                </Grid>
                <Grid item xs={4}>
                    <StatCard label="Last 7 days" value={fmtNum(signups.last_7d)} mono />
                </Grid>
                <Grid item xs={4}>
                    <StatCard label="Last 30 days" value={fmtNum(signups.last_30d)} mono />
                </Grid>
            </Grid>

            {/* Logins */}
            <SectionHeader icon={LoginIcon}>Active logins</SectionHeader>
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} md={3}>
                    <StatCard label="Daily (24h)" value={fmtNum(logins.dau)} mono />
                </Grid>
                <Grid item xs={6} md={3}>
                    <StatCard label="Weekly (7d)" value={fmtNum(logins.wau)} mono />
                </Grid>
                <Grid item xs={6} md={3}>
                    <StatCard label="Monthly (30d)" value={fmtNum(logins.mau)} mono />
                </Grid>
                <Grid item xs={6} md={3}>
                    <StatCard label="Yearly (365d)" value={fmtNum(logins.yau)} mono />
                </Grid>
            </Grid>
            <Surface variant="subtle" padding={1.5} sx={{ mb: 4 }}>
                <Typography variant="caption" sx={{ color: 'text.muted' }}>
                    Login metrics populate from the pre-token-generation trigger.
                    Users who haven't signed in since the trigger was deployed
                    aren't counted yet.
                </Typography>
            </Surface>

            {/* Revenue */}
            <SectionHeader icon={AttachMoneyIcon}>Revenue</SectionHeader>
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <StatCard
                        label="Active subscriptions"
                        value={fmtNum(revenue.active_subscriptions)}
                        mono
                    />
                </Grid>
                <Grid item xs={6}>
                    <StatCard
                        label="MRR"
                        value={fmtUsd(revenue.mrr_cents)}
                        tone="positive"
                        mono
                    />
                </Grid>
            </Grid>
            {revenue.error && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                    Stripe fetch failed: {revenue.error}
                </Alert>
            )}
        </>
    );
}
